import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Plus, Edit, Eye, Users, Percent } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getShifts } from '../../services/api/callCenterShifts';
import { getUsers } from '../../services/api/users';
import CallCenterShiftModal from './components/CallCenterShiftModal';
import EditSlabsModal from './components/EditSlabsModal';
import styles from './CallCenterShiftsPage.module.css';

const CallCenterShiftsPage = () => {
  const { t, formatDate, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [shifts, setShifts] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    total: 0,
    pageSizeOptions: [10, 15, 25, 50],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    shiftDateRange: { from: '', to: '' },
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedShift, setSelectedShift] = useState(null);
  const [showSlabsModal, setShowSlabsModal] = useState(false);

  useEffect(() => {
    loadBusinessUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadShifts();
  }, [pagination.currentPage, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBusinessUsers = async () => {
    try {
      const data = await getUsers({ status: 'true', page: 1, limit: 200 });
      setBusinessUsers(data.users || []);
    } catch (err) {
      console.error('Error loading business users:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToLoadUsers'));
    }
  };

  const loadShifts = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      };
      if (filters.shiftDateRange?.from) params.dateFrom = filters.shiftDateRange.from;
      if (filters.shiftDateRange?.to) params.dateTo = filters.shiftDateRange.to;

      const data = await getShifts(params);
      setShifts(data.shifts || []);
      setPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading shifts:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToLoadShifts'));
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ shiftDateRange: { from: '', to: '' } });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => !!filters.shiftDateRange?.from || !!filters.shiftDateRange?.to;

  const handleCreate = () => {
    setModalMode('create');
    setSelectedShift(null);
    setShowModal(true);
  };

  const handleEdit = (shift) => {
    setModalMode('edit');
    setSelectedShift(shift);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedShift(null);
    loadShifts();
  };

  const filterDefinitions = [
    {
      key: 'shiftDateRange',
      label: t('callCenterShifts.filterByDateRange'),
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker
          value={value || { from: '', to: '' }}
          onChange={onChange}
          placeholder={t('callCenterShifts.allShiftDates')}
        />
      ),
    },
  ];

  const columns = [
    {
      key: 'shiftDate',
      header: t('callCenterShifts.shiftDate'),
      sortable: false,
      width: '150px',
      render: (row) => <div className={styles.dateCell}>{formatDate(row.shiftDate)}</div>,
    },
    {
      key: 'staffCount',
      header: t('callCenterShifts.staffCount'),
      width: '120px',
      render: (row) => (
        <div className={styles.staffCountCell}>
          <Users size={14} />
          <span>{row.staff?.length || 0}</span>
        </div>
      ),
    },
    {
      key: 'totalTarget',
      header: t('callCenterShifts.totalTarget'),
      width: '150px',
      render: (row) => (row.totalTarget != null ? formatCurrency(row.totalTarget) : <span className={styles.na}>—</span>),
    },
    {
      key: 'achievedSalesValue',
      header: t('callCenterShifts.achievedSalesValue'),
      width: '220px',
      render: (row) => {
        if (row.achievedSalesValue == null) return <span className={styles.na}>—</span>;
        const isAchieved = parseFloat(row.achievedSalesValue) >= parseFloat(row.totalTarget || 0);
        return (
          <span className={`${isAchieved ? styles.achieved : styles.notAchieved} ${row.isEstimate ? styles.estimateValue : ''}`}>
            {formatCurrency(row.achievedSalesValue)}
            {row.isEstimate && <span className={styles.estimateBadge}>{t('callCenterShifts.estimate')}</span>}
          </span>
        );
      },
    },
    {
      key: 'totalCommission',
      header: t('callCenterShifts.totalCommission'),
      width: '200px',
      render: (row) => {
        if (row.totalCommission == null) return <span className={styles.na}>—</span>;
        if (!row.isEstimate) return formatCurrency(row.totalCommission);
        return (
          <span className={styles.estimateValue}>
            {formatCurrency(row.totalCommission)}
            <span className={styles.estimateBadge}>{t('callCenterShifts.estimate')}</span>
          </span>
        );
      },
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        <Link
          to={`/call-center/shifts/${row.id}`}
          onClick={(e) => e.stopPropagation()}
          className={styles.actionButton}
          title={t('callCenterShifts.viewDetails')}
        >
          <Eye size={16} />
        </Link>
        {hasPermission('manage_callcenter_shifts') && (
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className={styles.actionButton}
            title={t('callCenterShifts.edit')}
          >
            <Edit size={16} />
          </button>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.callCenterShiftsPage}>
      <PageHeader
        title={t('callCenterShifts.pageTitle')}
        description={t('callCenterShifts.pageDescription')}
        icon={Phone}
        actions={
          (hasPermission('manage_callcenter_shifts') || hasPermission('manage_callcenter_commission_slabs')) && (
            <div className={styles.headerActions}>
              {hasPermission('manage_callcenter_commission_slabs') && (
                <button onClick={() => setShowSlabsModal(true)} className={styles.secondaryButton}>
                  <Percent size={20} />
                  {t('callCenterShifts.editSlabs')}
                </button>
              )}
              {hasPermission('manage_callcenter_shifts') && (
                <button onClick={handleCreate} className={styles.primaryButton}>
                  <Plus size={20} />
                  {t('callCenterShifts.newShift')}
                </button>
              )}
            </div>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={shifts}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/call-center/shifts/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={hasActiveFilters() ? t('callCenterShifts.noShiftsFound') : t('callCenterShifts.noShiftsYet')}
        emptyDescription={
          hasActiveFilters()
            ? t('callCenterShifts.noShiftsFoundDescription')
            : t('callCenterShifts.noShiftsYetDescription')
        }
        emptyIcon={<Phone size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('manage_callcenter_shifts') ? (
            <button onClick={handleCreate} className={styles.primaryButton}>
              <Plus size={18} />
              {t('callCenterShifts.newShift')}
            </button>
          ) : null
        }
      />

      {showModal && (
        <CallCenterShiftModal
          mode={modalMode}
          shift={selectedShift}
          businessUsers={businessUsers}
          onClose={() => { setShowModal(false); setSelectedShift(null); }}
          onSuccess={handleModalSuccess}
        />
      )}

      {showSlabsModal && (
        <EditSlabsModal
          onClose={() => setShowSlabsModal(false)}
          onSuccess={() => { setShowSlabsModal(false); loadShifts(); }}
        />
      )}
    </div>
  );
};

export default CallCenterShiftsPage;
